import {
  getDurationFromTimeRangeStart,
  getDurationStringMilliseconds,
  isTimeRangeInputValueAbsolute,
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
  ONE_SECOND,
  TimeRangeInputValue,
} from '@chronosphereio/core';
import { useMemo } from 'react';
import {
  UseTimeRangeLastLoaded,
  UseTimeRangeLastLoadedProps,
  useTimeRangeLastLoaded,
} from '@/plugin-system/runtime/TimeRangeProviderV2';

type UseTimeRangeLastLoadedTimePickerProps = Pick<UseTimeRangeLastLoadedProps, 'disabled'> & {
  timeRange: TimeRangeInputValue;
};

type ShowLastLoadedMessageConfig = {
  /**
   * Minimum time range (inclusive) that should trigger this option (e.g. set to 1 hour in
   * milliseconds to trigger this option for time ranges that are 1 hour or greater).
   */
  minTimeRangeMs: number;
  /**
   * Minimum time since data was loaded to show the "last loaded" message. Useful for avoiding
   * showing this message too soon for larger time windows.
   */
  minLoadingDurationToShow: number;
  /**
   * How often the last loaded information should be updated.
   */
  updateIntervalMs: number;
};

/**
 * List of potential configuration options for the last loaded message. These impact:
 * - How often the last loaded information is updated.
 * - The minimum duration to show the "last loaded" message.
 *
 * These configurations MUST be kept with the `minTimeRangeMs` in decreasing order for the
 * logic to work properly.
 */
const showLastLoadedMessageOptions: ShowLastLoadedMessageConfig[] = [
  // If time range >= 1 day, show message after time is stale by 1 hour and update every minute.
  {
    minTimeRangeMs: 1 * ONE_DAY,
    minLoadingDurationToShow: 1 * ONE_HOUR,
    updateIntervalMs: 1 * ONE_MINUTE,
  },
  // If time range >= 12 hours, show message after time is stale by 30 minutes and update every
  // minute.
  {
    minTimeRangeMs: 12 * ONE_HOUR,
    minLoadingDurationToShow: 30 * ONE_MINUTE,
    updateIntervalMs: 1 * ONE_MINUTE,
  },
  // If time range >= 15 minutes, show message after time is stale by 10 minutes and update every
  // minute.
  {
    minTimeRangeMs: 15 * ONE_MINUTE,
    minLoadingDurationToShow: 10 * ONE_MINUTE,
    updateIntervalMs: 1 * ONE_MINUTE,
  },
  // Otherwise, show message after time is stale and update every 15 seconds.
  {
    minTimeRangeMs: 1,
    minLoadingDurationToShow: 1,
    updateIntervalMs: 15 * ONE_SECOND,
  },
];

/**
 * Opinionated version of `useTimeRangeLastLoaded` for the `DateTimeRangePicker`.
 * It modifies the update interval and when messages are shown based on the duration of the time
 * range to avoid overwhelming the user when the information is not as useful.
 */
export const useTimeRangeLastLoadedTimePicker = ({
  disabled,
  timeRange,
}: UseTimeRangeLastLoadedTimePickerProps): UseTimeRangeLastLoaded => {
  const matchingShowLastLoadedMessageOption = useMemo(() => {
    if (isTimeRangeInputValueAbsolute(timeRange)) {
      return undefined;
    }

    const pastDuration = getDurationFromTimeRangeStart(timeRange);
    const rangeMs = getDurationStringMilliseconds(pastDuration);
    if (!rangeMs) {
      return undefined;
    }

    return showLastLoadedMessageOptions.find((opt) => rangeMs >= opt.minTimeRangeMs);
  }, [timeRange]);

  const lastLoaded = useTimeRangeLastLoaded({
    disabled,
    updateIntervalMs: matchingShowLastLoadedMessageOption?.updateIntervalMs,
  });

  const shouldShowLastLoadedMessage =
    !!matchingShowLastLoadedMessageOption &&
    !!lastLoaded?.lastLoadedMs &&
    lastLoaded.lastLoadedMs >= matchingShowLastLoadedMessageOption.minLoadingDurationToShow;

  if (shouldShowLastLoadedMessage) {
    // If ready for message to be shown, pass along results as is.
    return lastLoaded;
  }

  // If not ready to show message, return disabled result.
  return {
    enabled: false,
  };
};
